import { useEffect, useRef, useState } from "react";

// The footer's brand mark, which builds itself once and then stays built.
//
// Dom made a logo build animation and asked for it "on the bottom of the
// website... when people scroll... then keep it as a still once it's done".
// Three things about the file decide how this is written.
//
// It has no alpha channel and a near-white ground, while the footer stands on
// the evening photograph. Dropped in plainly you get a white rectangle on a
// photo. `mix-blend-mode: multiply` erases the white and leaves the dark mark,
// which is why the CSS does that rather than us keying a second encode for
// Safari.
//
// Its opening sweep is larger than the finished logo and runs off all four
// edges of the source frame; measured, the content only sits inside the mark's
// own footprint from 1.75s. So this uses a version cropped and trimmed to start
// there. The full six seconds needs a whole screen and belongs on social.
//
// And it ends on the stacked lockup, mark over CHLEB AI over the tagline, which
// is a different shape from the wide wordmark the footer used to show. The
// poster is that same final frame, so the still state, the reduced-motion state
// and the no-JS state are all identical to where the animation lands.
//
// It plays once per session, not once per scroll. A logo that rebuilds itself
// every time you reach the bottom of a page is a fidget, not a brand.
const SEEN = "chleb-footer-mark-played";

export function FooterMark() {
  const wrap = useRef<HTMLSpanElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) return;
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN) === "1";
    } catch {
      // Private browsing throws on read. Treat it as "not seen": the cost of
      // being wrong is one extra play, not a broken footer.
    }
    if (seen) return;
    const el = wrap.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setPlay(true);
        try {
          sessionStorage.setItem(SEEN, "1");
        } catch {
          /* nothing to do; it simply plays again next page */
        }
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!play) return;
    const v = video.current;
    if (!v) return;
    // If autoplay is refused the poster is already the finished lockup, so the
    // footer still shows the right thing. Nothing to recover from.
    void v.play().catch(() => {});
  }, [play]);

  return (
    <span className="rb-footer-mark" ref={wrap}>
      {play ? (
        <video
          ref={video}
          className="rb-footer-mark-clip"
          src="/video/logo-build.mp4"
          poster="/video/logo-build-poster.webp"
          width={440}
          height={440}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          // Freeze on the last frame: no loop, and the element keeps painting
          // whatever it stopped on.
          onEnded={(e) => {
            e.currentTarget.pause();
          }}
        />
      ) : (
        <img
          src="/video/logo-build-poster.webp"
          alt="Chleb AI"
          width={440}
          height={440}
          decoding="async"
        />
      )}
    </span>
  );
}
